import React from "react";
import { useTranslation } from "react-i18next";
import { changeLanguage, SupportedLanguage } from "./index";

// Flag buttons in the style of irei.is. The flags are inline SVG rather than
// emoji: Windows has no flag emoji and renders "GB"/"IS" letters instead.
const FlagUK: React.FC = () => (
  <svg viewBox="0 0 60 30" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
    <rect width="60" height="30" fill="#012169" />
    <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
    <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="2" />
    <path d="M30,0 V30 M0,15 H60" stroke="#fff" strokeWidth="10" />
    <path d="M30,0 V30 M0,15 H60" stroke="#C8102E" strokeWidth="6" />
  </svg>
);

const FlagIS: React.FC = () => (
  <svg viewBox="0 0 25 18" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
    <rect width="25" height="18" fill="#02529C" />
    <path d="M9,0 V18 M0,9 H25" stroke="#fff" strokeWidth="4" />
    <path d="M9,0 V18 M0,9 H25" stroke="#DC1E35" strokeWidth="2" />
  </svg>
);

// Names are endonyms ("Íslenska", not "Icelandic"), so they are not translated.
const LANGUAGES: { code: SupportedLanguage; label: string; name: string; Flag: React.FC }[] = [
  { code: "en", label: "EN", name: "English", Flag: FlagUK },
  { code: "is", label: "IS", name: "Íslenska", Flag: FlagIS },
];

interface LanguageSwitcherProps {
  className?: string;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ className }) => {
  const { t, i18n } = useTranslation("common");

  return (
    <div
      role="group"
      aria-label={t("languageSwitcher.label")}
      className={`inline-flex shrink-0 overflow-hidden rounded-full border border-ink/15 ${className ?? ""}`}
    >
      {LANGUAGES.map(({ code, label, name, Flag }) => {
        const active = i18n.language === code;
        return (
          <button
            key={code}
            type="button"
            lang={code}
            title={name}
            aria-label={name}
            aria-pressed={active}
            onClick={() => changeLanguage(code)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold transition-colors ${
              active ? "bg-ink text-white" : "text-ink-600 hover:bg-white hover:text-ink"
            }`}
          >
            <span className="inline-flex h-3 w-[18px] shrink-0 overflow-hidden rounded-[2px] ring-1 ring-ink/10 [&>svg]:h-full [&>svg]:w-full">
              <Flag />
            </span>
            {label}
          </button>
        );
      })}
    </div>
  );
};
